import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaPaperPlane } from "react-icons/fa";
import { TextDocumentDto } from "../../../../../autogen/bff-api";
import { PdfPreview } from "../../../../../common/documents/PdfPreview";
import { renderTextDocumentAsPdf } from "../../../../../common/input/TipTap/downloadTextDocument";

interface Props {
  textDocuments: TextDocumentDto[];
  isSending: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

export const SigningPreviewModal = ({ textDocuments, isSending, onBack, onConfirm }: Props) => {
  const [selectedId, setSelectedId] = useState(textDocuments[0].id);
  const [pdfByTextDocumentId, setPdfByTextDocumentId] = useState<Record<string, Blob>>({});
  const [failedTextDocumentIds, setFailedTextDocumentIds] = useState<string[]>([]);

  const selected = useMemo(
    () => textDocuments.find((d) => d.id === selectedId) ?? textDocuments[0],
    [textDocuments, selectedId]
  );

  const selectedPdf = pdfByTextDocumentId[selected.id];
  const hasSelectedFailed = failedTextDocumentIds.includes(selected.id);

  const renderPdf = useCallback(async () => {
    try {
      const pdf = await renderTextDocumentAsPdf({ fileName: selected.name, content: selected.text });
      setPdfByTextDocumentId((previous) => ({ ...previous, [selected.id]: pdf }));
    } catch {
      setFailedTextDocumentIds((previous) => [...previous, selected.id]);
    }
  }, [selected]);

  const retryRenderingSelected = () =>
    setFailedTextDocumentIds((previous) => previous.filter((id) => id !== selected.id));

  useEffect(() => {
    if (!selectedPdf && !hasSelectedFailed) renderPdf();
  }, [selectedPdf, hasSelectedFailed, renderPdf]);

  return (
    <Modal isOpen={true} onClose={onBack} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent height="95%">
        <ModalHeader pb="2">
          <Text>{t("Check the document before sending")}</Text>
          <Text fontSize="sm" fontWeight="normal" color="smMuted">
            {t("This is exactly the PDF the recipients will receive and sign.")}
          </Text>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap="3" pb="6">
          {textDocuments.length > 1 && (
            <Flex gap="2" flexWrap="wrap">
              {textDocuments.map((textDocument) => (
                <Button
                  key={textDocument.id}
                  size="xs"
                  variant={textDocument.id === selected.id ? "solid" : "outline"}
                  colorScheme={textDocument.id === selected.id ? "blue" : "gray"}
                  onClick={() => setSelectedId(textDocument.id)}
                >
                  {textDocument.name}
                </Button>
              ))}
            </Flex>
          )}

          <Box flex="1" minHeight="0" border="1px solid" borderColor="smBorder" rounded="md" overflow="hidden">
            {hasSelectedFailed ? (
              <Center height="full" flexDirection="column" gap="3" px="6" textAlign="center">
                <Text fontSize="sm">{t("The preview could not be generated")}</Text>
                <Button size="sm" variant="outline" onClick={retryRenderingSelected}>
                  {t("Try again")}
                </Button>
              </Center>
            ) : (
              <PdfPreview source={selectedPdf} />
            )}
          </Box>

          <Flex justifyContent="space-between" alignItems="center">
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Icon as={FaArrowLeft} w="13px" h="13px" />}
              onClick={onBack}
              isDisabled={isSending}
            >
              {t("Back")}
            </Button>
            <Button
              size="sm"
              variant="solid"
              colorScheme="blue"
              leftIcon={<Icon as={FaPaperPlane} w="15px" h="15px" />}
              onClick={onConfirm}
              isLoading={isSending}
              isDisabled={!selectedPdf}
            >
              {t("Confirm and send for signing")}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
